import type { AxiosError } from 'axios'
import apiClient from 'BrokerTsLib/src/Utils/apiClient'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { Apis } from '@/appSettings'
import type { PpzAuthorization, PpzAuthorizationGrid, PpzAuthorizationGridItem, PpzAuthorizationPagedQuery, PpzAuthorizationUpdate } from '@/dtos'

import { useUrl } from '.'

export const useGetByUser = (userId: string) => {
  const url = useUrl(Apis.ppzAuthorization)

  return useQuery<PpzAuthorizationGridItem[], AxiosError>(
    ['ppzAuthorizationByUser', userId],
    async () => {

      const result = await apiClient.get<PpzAuthorizationGridItem[]>(`${url}/getByUser`, {
        params: { userId },
      })

      return result.data
    },
  )
}

export const useGetAll = (query: PpzAuthorizationPagedQuery) => {
  const url = useUrl(Apis.ppzAuthorization)

  return useQuery<PpzAuthorizationGrid, AxiosError>(
    ['ppzAuthorizationQuery', query],
    async () => {
      const result = await apiClient.post<PpzAuthorizationGrid>(`${url}/getAll`, query)

      return result.data
    },
  )
}

export const useCreate = () => {
  const url = useUrl(Apis.ppzAuthorization)
  const queryClient = useQueryClient()

  return useMutation<PpzAuthorization, AxiosError, FormData>([], async (variables: FormData) => {
    const result = await apiClient.post<PpzAuthorization>(`${url}/create`, variables)

    return result.data
  }, {
    onSuccess: (ppzAuthorization) => {
      queryClient.setQueryData(['ppzAuthorization', ppzAuthorization.id], ppzAuthorization)
    }
  })
}

export const useAddFiles = (id: number) => {
  const url = useUrl(Apis.ppzAuthorization)
  const queryClient = useQueryClient()

  return useMutation([], async (variables: FormData) => {
    const result = await apiClient.post<PpzAuthorization>(`${url}/addFiles`, variables)

    return result.data
  }, {
    onSuccess: (ppzAuthorization) => {
      queryClient.setQueryData(['ppzAuthorization', id], ppzAuthorization)
    }
  })
}

export const useGet = (ppzId: number) => {
  const url = useUrl(Apis.ppzAuthorization)

  return useQuery<PpzAuthorization, AxiosError>(
    ['ppzAuthorization', ppzId],
    async () => {
      const result = await apiClient.get<PpzAuthorization>(`${url}/get`, { params: { ppzId } })

      return result.data
    },
  )
}

export const useGetDownloadFileUrl = (ppzId: number, fileName: string) => {
  const url = useUrl(Apis.ppzAuthorization)

  return `${url}/getFile?ppzId=${ppzId}&fileName=${fileName}`
}

export const useApprove = (id: number) => {
  const url = useUrl(Apis.ppzAuthorization)
  const queryClient = useQueryClient()

  return useMutation<PpzAuthorization, AxiosError, void>([], async () => {
    const result = await apiClient.post<PpzAuthorization>(`${url}/approve`, { id })

    return result.data
  }, {
    onSuccess: (ppzAuthorization) => {
      queryClient.setQueryData(['ppzAuthorization', ppzAuthorization.id], ppzAuthorization)
      queryClient.invalidateQueries(['ppzAuthorizationQuery'])
      queryClient.invalidateQueries(['ppzAuthorizationByUser', ppzAuthorization.ppzUserId])
    }
  })
}

export const useUpdateFileName = (id: number, fileName: string) => {
  const url = useUrl(Apis.ppzAuthorization)
  const queryClient = useQueryClient()

  return useMutation((variables: { newFileName: string }) => apiClient.post(`${url}/updateFileName`, { id, fileName, newFileName: variables.newFileName }), {
    onSuccess: (_, { newFileName }) => {
      queryClient.setQueryData<PpzAuthorization | undefined>(['ppzAuthorization', id], (ppzAuthorization) => ppzAuthorization && ({
        ...ppzAuthorization,
        files: ppzAuthorization.files.map(f => f === fileName ? newFileName : f)
      }))
    }
  })
}

export const useUpdate = (ppzId: number) => {
  const url = useUrl(Apis.ppzAuthorization)
  const queryClient = useQueryClient()

  return useMutation((variables: PpzAuthorizationUpdate) => apiClient.post(`${url}/update`, variables), {
    onSuccess: (_, variables) => {
      queryClient.setQueryData<PpzAuthorization | undefined>(['ppzAuthorization', ppzId], (ppzAuthorization) => ppzAuthorization && ({ ...ppzAuthorization, ...variables }))
    }
  })
}

export const useDeleteFile = (id: number, fileName: string) => {
  const url = useUrl(Apis.ppzAuthorization)
  const queryClient = useQueryClient()

  return useMutation(() => apiClient.delete(`${url}/deleteFile`, { params: { id, fileName } }), {
    onSuccess: () => {
      queryClient.setQueryData<PpzAuthorization | undefined>(['ppzAuthorization', id], (ppzAuthorization) => ppzAuthorization && ({
        ...ppzAuthorization,
        files: ppzAuthorization.files.filter(f => f !== fileName)
      }))
    }
  })
}

export const useSendEmail = (id: number) => {
  const url = useUrl(Apis.ppzAuthorization)
  return useMutation((type: number) => apiClient.post(`${url}/sendEmail`, { id, type }))
}